import React from 'react';
import { Platform, StyleSheet, Text, View } from 'react-native';
import { PRIMARY_BLUE, WARNING_RED } from '../../../constants/colors';
import { FontFamily } from '../../../../../constants/fonts';
import { CrewingVerticalScrollView } from './webScrollStyles';

export interface BulletChartRow {
  label: string;
  value: number;
  target?: number;
  marker?: number;
}

interface ToolAdoptionBulletCardProps {
  title: string;
  subtitle?: string;
  description?: string;
  rows: BulletChartRow[];
  /** Prior-period rows (same labels) used to render a "vs prior" delta next to each value. */
  priorRows?: BulletChartRow[];
  max?: number;
  target?: number;
}

const ToolAdoptionBulletCard: React.FC<ToolAdoptionBulletCardProps> = ({
  title,
  subtitle,
  description,
  rows,
  priorRows = [],
  max = 100,
  target = 80,
}) => {
  const toPercent = (value: number) => Math.max(0, Math.min(100, (value / max) * 100));
  const barWidth = (value: number): `${number}%` => `${toPercent(value)}%`;
  const priorByLabel = new Map(priorRows.map((row) => [row.label, row]));
  const chartContent = (
    <View style={styles.chartArea}>
      {rows.map((row, index) => {
        // Always compare against the single fixed target (80%) — a per-row target would
        // draw a misaligned marker/color that no longer matches the "target 80%" label.
        const isBelowTarget = row.value < target;
        const markerLeft = Math.max(0, Math.min(100, toPercent(target) - 0.75));
        const prior = priorByLabel.get(row.label);
        const delta = prior ? row.value - prior.value : undefined;
        // Match Perfect Truck Execution: keep values inside the fill unless it is too narrow
        // to display the label legibly.
        const showValueInsideBar = toPercent(row.value) >= 15;
        return (
          <View key={`${row.label}-${index}`} style={styles.row}>
            <View style={styles.rowLabelCol}>
              <Text style={styles.rowLabel} numberOfLines={1}>{row.label}</Text>
              {delta != null && !isNaN(delta) && Math.abs(delta) >= 0.05 && (
                <Text style={[styles.rowDelta, delta > 0 ? styles.rowDeltaUp : styles.rowDeltaDown]}>
                  {`${delta > 0 ? '▲' : '▼'}${Math.abs(delta).toFixed(1)} vs prior`}
                </Text>
              )}
            </View>

            <View style={styles.rowChartCol}>
              <View style={styles.track}>
                <View
                  style={[
                    styles.filledBar,
                    {
                      width: barWidth(row.value),
                      backgroundColor: isBelowTarget ? WARNING_RED : PRIMARY_BLUE,
                    },
                  ]}
                >
                  {showValueInsideBar ? (
                    <Text style={styles.insideBarValue}>{row.value.toFixed(1)}%</Text>
                  ) : null}
                </View>

                <View
                  style={[
                    styles.targetMarker,
                    {
                      left: `${markerLeft}%`,
                    },
                  ]}
                />
                {!showValueInsideBar ? (
                  <Text
                    style={[
                      styles.outsideFillValue,
                      { left: `${toPercent(row.value)}%`, color: isBelowTarget ? WARNING_RED : PRIMARY_BLUE },
                    ]}
                  >
                    {row.value.toFixed(1)}%
                  </Text>
                ) : null}
              </View>
            </View>
          </View>
        );
      })}
    </View>
  );

  return (
    <View style={styles.card}>
      <View style={[styles.headerRow, Platform.OS === 'web' && styles.headerRowWeb]}>
        <View style={[styles.titleRow, Platform.OS === 'web' && styles.titleRowWeb]}>
          <Text style={styles.title}>{title}</Text>
          {subtitle && <Text style={styles.subtitle}>{subtitle}</Text>}
        </View>
        {description && <Text style={[styles.description, Platform.OS === 'web' && styles.descriptionWeb]}>{description}</Text>}
      </View>

      {/* Legend */}
      <View style={styles.legend}>
        <View style={styles.legendItem}>
          <View style={[styles.legendSwatch, { backgroundColor: PRIMARY_BLUE }]} />
          <Text style={styles.legendText}>At / Above Target</Text>
        </View>
        <View style={styles.legendItem}>
          <View style={[styles.legendSwatch, { backgroundColor: WARNING_RED }]} />
          <Text style={styles.legendText}>Below Target</Text>
        </View>
        <View style={styles.legendItem}>
          <View style={styles.legendDash} />
          <Text style={styles.legendText}>Target {target}%</Text>
        </View>
      </View>

      {/* Chart */}
      <View style={styles.chartViewport}>
        {rows.length > 6 ? (
          <CrewingVerticalScrollView nestedScrollEnabled showsVerticalScrollIndicator>
            {chartContent}
          </CrewingVerticalScrollView>
        ) : chartContent}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 16,
    flex: 1,
    minHeight: 360,
    borderWidth: 1,
    borderColor: '#E3E9F3',
    shadowColor: '#A8B4C5',
    shadowOpacity: 0.08,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 2 },
    elevation: 1,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 10,
    flexWrap: 'wrap',
    width: '100%',
  },
  titleRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    columnGap: 4,
    width: '100%',
  },
  title: {
    fontSize: 16,
    lineHeight: 20,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    color: '#1A1D21',
  },
  subtitle: {
    fontSize: 12,
    lineHeight: 16,
    fontFamily: FontFamily.regular,
    fontWeight: '400',
    color: '#838383',
  },
  description: {
    fontSize: 12,
    lineHeight: 16,
    fontFamily: FontFamily.regular,
    fontWeight: '400',
    color: '#838383',
    width: '100%',
    marginTop: 4,
  },
  headerRowWeb: {
    flexWrap: 'nowrap',
    alignItems: 'center',
  },
  titleRowWeb: {
    flex: 1,
    width: 'auto',
    flexWrap: 'nowrap',
    columnGap: 4,
  },
  descriptionWeb: {
    width: 'auto',
    marginTop: 0,
    marginLeft: 12,
    flexShrink: 1,
    textAlign: 'right',
  },
  legend: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 12,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E3E9F3',
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  legendSwatch: {
    width: 12,
    height: 12,
    borderRadius: 2,
  },
  legendDash: {
    width: 14,
    borderTopWidth: 1.5,
    borderStyle: 'dashed',
    borderTopColor: '#9CA3AF',
  },
  legendText: {
    fontSize: 11,
    fontFamily: FontFamily.regular,
    color: '#838383',
  },
  chartViewport: {
    flex: 1,
    marginTop: 8,
    marginBottom: 10,
    minHeight: 0,
  },
  chartArea: {
    width: '100%',
  },
  row: {
    marginBottom: 12,
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 10,
  },
  rowLabelCol: {
    width: 98,
    alignItems: 'flex-start',
  },
  rowLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
    textAlign: 'left',
  },
  rowChartCol: {
    flex: 1,
  },
  track: {
    height: 28,
    backgroundColor: '#EAF1FB',
    borderRadius: 3,
    position: 'relative',
    overflow: 'visible',
    borderWidth: 1,
    borderColor: '#D6E2F3',
    marginBottom: 5,
  },
  filledBar: {
    height: '100%',
    borderRadius: 3,
    justifyContent: 'center',
    alignItems: 'flex-end',
    paddingRight: 8,
  },
  insideBarValue: {
    color: '#FFFFFF',
    fontSize: 11,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
  },
  outsideFillValue: {
    position: 'absolute',
    top: 0,
    height: '100%',
    marginLeft: 6,
    fontSize: 11,
    lineHeight: 28,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    textAlignVertical: 'center',
  },
  targetMarker: {
    position: 'absolute',
    top: -1,
    bottom: -1,
    width: 0,
    borderLeftWidth: 1.5,
    borderStyle: 'dashed',
    borderLeftColor: '#3B4A5E',
  },
  rowDelta: {
    marginTop: 2,
    fontSize: 9,
    fontFamily: FontFamily.medium,
    fontWeight: '600',
  },
  rowDeltaUp: {
    color: '#0B5FC6',
  },
  rowDeltaDown: {
    color: '#C12C01',
  },
});

export default ToolAdoptionBulletCard;
